import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  Renderer2,
  signal,
} from '@angular/core';

export interface SplitButtonOption {
  label: string;
  icon?: string;
  command?: () => void;
  disabled?: boolean;
}

let splitButtonMenuId = 0;

@Component({
  selector: 'app-split-button',
  template: `
    <div class="relative flex w-full min-w-0 items-stretch">
      <button
        type="button"
        [class]="buttonClasses()"
        (click)="onClick.emit($event); closeOptions()"
        [disabled]="disabled()"
        [attr.aria-label]="ariaLabel() || label()"
      >
        @if (icon()) {
          <i [class]="icon() + ' shrink-0'" aria-hidden="true"></i>
        }
        @if (label()) {
          <span class="truncate">{{ label() }}</span>
        }
      </button>
      <button
        type="button"
        [class]="dropdownButtonClasses()"
        (click)="toggleOptions($event)"
        [disabled]="disabled()"
        [attr.aria-expanded]="open()"
        [attr.aria-controls]="menuId"
        aria-haspopup="menu"
        aria-label="Más opciones"
        data-split-button-trigger
      >
        <i
          class="bi bi-chevron-down transition-transform duration-150"
          [class.rotate-180]="open()"
          aria-hidden="true"
        ></i>
      </button>
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        max-width: 100%;
        vertical-align: middle;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiSplitButton implements OnDestroy {
  readonly label = input<string>('');
  readonly icon = input<string>('');
  readonly options = input<SplitButtonOption[]>([]);
  readonly variant = input<string>('primary');
  readonly size = input<string>('md');
  readonly disabled = input<boolean>(false);
  readonly ariaLabel = input<string>('');

  readonly onClick = output<MouseEvent>();

  protected readonly menuId = `split-button-menu-${++splitButtonMenuId}`;
  protected open = signal(false);

  private el = inject<ElementRef<HTMLElement>>(ElementRef);
  private renderer = inject(Renderer2);
  private dropdownEl: HTMLElement | null = null;
  private cleanupListeners: Array<() => void> = [];

  protected buttonClasses(): string {
    return `min-w-0 flex-1 rounded-l-lg border-r-0 ${this.commonClasses()}`;
  }

  protected dropdownButtonClasses(): string {
    return `w-10 shrink-0 rounded-r-lg ${this.commonClasses()}`;
  }

  private commonClasses(): string {
    const variants: Record<string, string> = {
      primary: 'bg-accent-600 text-white border border-accent-600 hover:bg-accent-500',
      secondary: 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200',
      success: 'bg-teal-600 text-white border border-teal-600 hover:bg-teal-500',
      info: 'bg-sky-600 text-white border border-sky-600 hover:bg-sky-500',
      danger: 'bg-rose-600 text-white border border-rose-600 hover:bg-rose-500',
    };
    const sizes: Record<string, string> = {
      xs: 'min-h-8 px-2 py-1 text-xs',
      sm: 'min-h-9 px-3 py-1.5 text-sm',
      md: 'min-h-11 px-4 py-2 text-sm',
      lg: 'min-h-12 px-5 py-2.5 text-base',
    };
    const variantCls = variants[this.variant()] || variants['primary'];
    const sizeCls = sizes[this.size()] || sizes['md'];
    return `inline-flex touch-manipulation items-center justify-center gap-1.5 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:z-10 disabled:cursor-not-allowed disabled:opacity-50 ${variantCls} ${sizeCls}`;
  }

  protected toggleOptions(event: MouseEvent): void {
    event.stopPropagation();
    if (this.open()) {
      this.closeOptions();
    } else {
      this.openOptions();
    }
  }

  private openOptions(): void {
    this.open.set(true);
    this.createDropdown();
  }

  closeOptions(): void {
    if (!this.open() && !this.dropdownEl) return;
    this.open.set(false);
    this.destroyDropdown();
  }

  private createDropdown(): void {
    this.destroyDropdown();

    const trigger = this.el.nativeElement.querySelector<HTMLButtonElement>(
      '[data-split-button-trigger]',
    );
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const margin = 8;
    const preferredWidth = 224;
    const width = Math.min(preferredWidth, window.innerWidth - margin * 2);

    const spaceBelow = window.innerHeight - rect.bottom - margin * 2;
    const spaceAbove = rect.top - margin * 2;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(openUp ? spaceAbove : spaceBelow, 320));
    const left = Math.max(
      margin,
      Math.min(rect.right - width, window.innerWidth - width - margin),
    );

    this.dropdownEl = this.renderer.createElement('div');
    this.renderer.setAttribute(this.dropdownEl, 'id', this.menuId);
    this.renderer.setAttribute(this.dropdownEl, 'role', 'menu');
    this.renderer.setAttribute(
      this.dropdownEl,
      'class',
      'fixed z-[99999] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white py-1 shadow-lg animate-fade-in',
    );
    this.renderer.setStyle(this.dropdownEl, 'width', `${width}px`);
    this.renderer.setStyle(this.dropdownEl, 'left', `${left}px`);
    this.renderer.setStyle(this.dropdownEl, 'top', `${openUp ? rect.top - 4 : rect.bottom + 4}px`);
    this.renderer.setStyle(this.dropdownEl, 'max-height', `${maxHeight}px`);
    if (openUp) {
      this.renderer.setStyle(this.dropdownEl, 'transform', 'translateY(-100%)');
    }

    for (const option of this.options()) {
      const btn = this.renderer.createElement('button');
      this.renderer.setAttribute(btn, 'type', 'button');
      this.renderer.setAttribute(btn, 'role', 'menuitem');
      this.renderer.setAttribute(
        btn,
        'class',
        `flex w-full touch-manipulation items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 focus:bg-slate-50 focus:outline-none${
          option.disabled ? ' cursor-not-allowed opacity-40' : ''
        }`,
      );
      if (option.disabled) {
        this.renderer.setAttribute(btn, 'disabled', '');
      }

      if (option.icon) {
        const icon = this.renderer.createElement('i');
        this.renderer.setAttribute(icon, 'class', `${option.icon} shrink-0`);
        this.renderer.setAttribute(icon, 'aria-hidden', 'true');
        this.renderer.appendChild(btn, icon);
      }

      const label = this.renderer.createText(option.label);
      this.renderer.appendChild(btn, label);

      if (!option.disabled) {
        this.cleanupListeners.push(
          this.renderer.listen(btn, 'click', () => {
            this.closeOptions();
            option.command?.();
          }),
        );
      }

      this.renderer.appendChild(this.dropdownEl!, btn);
    }

    this.renderer.appendChild(document.body, this.dropdownEl);

    // El listener de "click fuera" se registra en el siguiente tick para no
    // cerrar el menú con el mismo click que lo abrió.
    const outsideId = setTimeout(() => {
      if (!this.dropdownEl) return;
      this.cleanupListeners.push(
        this.renderer.listen('document', 'click', (event: MouseEvent) => {
          if (this.dropdownEl && !this.dropdownEl.contains(event.target as Node)) {
            this.closeOptions();
          }
        }),
      );
    }, 0);

    const closeOnViewportChange = () => this.closeOptions();
    window.addEventListener('scroll', closeOnViewportChange, true);
    window.addEventListener('resize', closeOnViewportChange);
    this.cleanupListeners.push(() => {
      clearTimeout(outsideId);
      window.removeEventListener('scroll', closeOnViewportChange, true);
      window.removeEventListener('resize', closeOnViewportChange);
    });

    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      this.closeOptions();
      trigger.focus();
    };
    this.cleanupListeners.push(
      this.renderer.listen('document', 'keydown', onEscape),
    );
  }

  private destroyDropdown(): void {
    for (const cleanup of this.cleanupListeners.splice(0)) {
      cleanup();
    }
    if (this.dropdownEl) {
      if (this.dropdownEl.parentNode) {
        this.renderer.removeChild(document.body, this.dropdownEl);
      }
      this.dropdownEl = null;
    }
  }

  ngOnDestroy(): void {
    this.destroyDropdown();
  }
}
